import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import styled from 'styled-components';
import Button from '../components/Button';
import { APP_NAME } from '../constants';
import logoImage from '../assets/logo.png';
import backgroundImage from '../assets/home-page-background.jpg';
import appstoreButton from '../assets/appstore-button.png';
import playstoreButton from '../assets/playstore-button.png';

// Define styled components for the page
const PageContainer = styled.div`
  position: relative;
  width: 100%;
  min-height: 100vh;
  overflow-y: auto; /* Allow scrolling */
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const BackgroundImage = styled.div`
  position: fixed; /* Fixed position so it stays in place while scrolling */
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: url(${backgroundImage});
  background-size: cover;
  background-position: center;
  z-index: -1;
  filter: brightness(0.7); /* Darkened to improve text readability */
`;

const Header = styled.header`
  position: sticky; /* Make the header stick to the top */
  top: 0;
  left: 0;
  width: 100%;
  padding: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 10;
`;

const Logo = styled.img`
  height: 50px;
  
  @media (max-width: 480px) {
    height: 40px;
  }
`;

const ProfileIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.9);
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  
  i {
    font-size: 22px;
    color: #381f74;
  }
  
  @media (max-width: 480px) {
    width: 36px;
    height: 36px;
    
    i {
      font-size: 20px;
    }
  }
`;

const ContentContainer = styled.div`
  text-align: center;
  max-width: 800px;
  padding: 30px;
  border-radius: 20px;
  z-index: 1;
  margin: 80px auto 40px; /* Add margin to prevent overlap with header */
  
  @media (max-width: 768px) {
    padding: 20px;
    width: 90%;
    margin-top: 60px; /* Slightly less margin on smaller screens */
  }
`;

const Heading = styled.h1`
  font-size: 42px;
  font-weight: 700;
  margin-bottom: 20px;
  color: white;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  
  @media (max-width: 768px) {
    font-size: 36px;
  }
  
  @media (max-width: 480px) {
    font-size: 30px;
  }
`;

const Paragraph = styled.p`
  font-size: 18px;
  margin-bottom: 30px;
  line-height: 1.6;
  color: white;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
  font-weight: 500;
  
  @media (max-width: 480px) {
    font-size: 16px;
  }
`;

const AppNameSpan = styled.span`
  font-weight: bold;
  font-style: italic;
`;

const ButtonContainer = styled.div`
  width: 100%;
  max-width: 300px;
  margin: 0 auto 30px;
`;

const SubHeading = styled.h2`
  font-size: 28px;
  font-weight: 600;
  margin: 20px 0;
  color: white;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  
  @media (max-width: 480px) {
    font-size: 24px;
  }
`;

const AppStoreContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-bottom: 30px;
  
  @media (max-width: 480px) {
    gap: 10px;
  }
`;

const AppStoreImage = styled.img`
  height: 50px;
  
  @media (max-width: 480px) {
    height: 40px;
  }
`;

const BetaMessage = styled.p`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 20px;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  
  @media (max-width: 480px) {
    font-size: 12px;
  }
`;

const LandingPage: React.FC = () => {
  const navigate = useNavigate();

  const handleRegister = () => {
    navigate('/register');
  };

  const handleLogin = () => {
    navigate('/login');
  };

  const handleProfile = () => {
    navigate('/login');
  };
  return (
    <>
      <Helmet>
        <title>{APP_NAME} – Real Rewards, Not Points</title>
        <meta name="description" content="Ditch the points. Forget the stamps. Start earning real store credit every time you shop at participating restaurants, cafes, salons, and local stores." />
        <meta property="og:title" content={`${APP_NAME} – Real Rewards, Not Points`} />
        <meta property="og:description" content="Ditch the points. Forget the stamps. Start earning real store credit every time you shop at participating restaurants, cafes, salons, and local stores." />
        <meta property="og:image" content="/assets/Store Credit App.jpg" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${APP_NAME} – Real Rewards, Not Points`} />
        <meta name="twitter:description" content="Ditch the points. Forget the stamps. Start earning real store credit every time you shop at participating restaurants, cafes, salons, and local stores." />
        <meta name="twitter:image" content="/assets/Store Credit App.jpg" />
        <link rel="icon" href="/assets/Store Credit App.jpg" />
        <link rel="apple-touch-icon" href="/assets/Store Credit App.jpg" />
      </Helmet>
      <PageContainer>
      <BackgroundImage />
      
      <Header>
        <Logo src={logoImage} alt="Logo" />
        <ProfileIcon onClick={handleProfile}>
          <i className='bx bx-user'></i>
        </ProfileIcon>
      </Header>
      
      <ContentContainer>
        <Heading>Earn Store Credit. Not Points.</Heading>
        
        <Paragraph>
          Ditch the points. Forget the stamps. Start earning real store credit every time you shop at participating restaurants, cafes, salons, and local stores.
        </Paragraph>
        
        <ButtonContainer>
          <Button 
            text="Register" 
            onClick={handleRegister} 
            bgColor="#e9a43c" 
            textColor="#381f74"
          />
          
          <Button 
            text="Login" 
            onClick={handleLogin} 
            bgColor="#ffffff" 
            textColor="#381f74"
          />
        </ButtonContainer>
        
        <SubHeading>Get The App</SubHeading>
        
        <AppStoreContainer>
          <a href="https://play.google.com/store/apps/details?id=com.brownvincicode24.storecreditappc" target="_blank" rel="noopener noreferrer">
            <AppStoreImage 
              src={playstoreButton}
              alt="Get it on Google Play" 
            />
          </a>
          
          <a href="https://apps.apple.com/us/app/store-credit/id6746509517" target="_blank" rel="noopener noreferrer">
            <AppStoreImage 
              src={appstoreButton}
              alt="Download on the App Store" 
            />
          </a>
        </AppStoreContainer>
        
        <BetaMessage>
          <AppNameSpan>{APP_NAME}</AppNameSpan> is currently in BETA. If you run into any problems or have any
          questions or concerns, don't hesitate to <a href="/contact" style={{ color: 'rgba(255, 255, 255, 0.9)', textDecoration: 'underline' }}>contact us</a>.
        </BetaMessage>
        
        <BetaMessage>
          <strong>Developers:</strong> Check out our API documentation at{' '}
          <a 
            href="https://us-central1-storecreditapp.cloudfunctions.net/api/documentation" 
            target="_blank" 
            rel="noopener noreferrer"
            style={{ color: 'rgba(255, 255, 255, 0.9)', textDecoration: 'underline' }}
          >
            API Docs
          </a>
        </BetaMessage>      </ContentContainer>
    </PageContainer>
    </>
  );
};

export default LandingPage;